import { Availability } from '@c/dashboard/pages/ListingsPage';
import { CategoryDocument } from '@models/category';
import { logEvent } from '@util/analytics';
import { db, FirebaseCallable, functions, httpsCallable } from '@util/firebase';
import { BaseGetProductsArgs } from '@util/getPropsFromContext';
import { isMobile, nonNullable, normalize } from '@util/index';
import { logError } from '@util/logError';
import { ReasonTypes } from '@util/maps/report-reasons';
import { DataDepot } from 'app/data-depot/_components/DataDepotForm';
import { SalePageResponse } from 'app/sale/[curated-list]/page';
import {
  arrayRemove,
  arrayUnion,
  collection,
  collectionGroup,
  CollectionReference,
  deleteField,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  getDocsFromCache,
  increment,
  limit,
  loadBundle,
  namedQuery,
  orderBy,
  query,
  setDoc,
  startAfter,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import {
  CauseDocument,
  Condition,
  DataDepotProduct,
  GetForYouProductsOptions,
  GetProductArgs,
  ProductDocument,
  ProductLight,
  ProductResultsPaginatedByRef,
  productSchema,
  ProductSortOption,
  Variation,
  VEHICLE_CATEGORIES,
} from 'models/product';
import { ApiResponse, Bike, RideType } from 'models/shared';
import { Sports, UserDocument } from 'models/user';
import { isBot } from 'next/dist/server/web/spec-extension/user-agent';
import { stripHtml } from 'string-strip-html';
import {
  DocumentSchema,
  SearchParamsWithPreset,
} from 'typesense/lib/Typesense/Documents';
import { cleanLocation, DEFAULT_BREADCRUMBS, formatBreadcrumbs } from '.';
import { getBrandByName } from '../brands/brands.service';
import { getCategoryByID } from '../categories';
import { getProductLight } from './products.helpers';
import {
  getForYouProductsFromTypesenseServer,
  getLiveListingsCountServer,
  getRelatedProductsServer,
} from './products.typesense';

export const DEFAULT_LIMIT: number = 12;
export const EUROPE_CODES = [
  'AT',
  'BE',
  'BG',
  'CY',
  'CZ',
  'DE',
  'DK',
  'EE',
  'ES',
  'FI',
  'FR',
  'GB',
  'GR',
  'HR',
  'HU',
  'IE',
  'IT',
  'LT',
  'LU',
  'LV',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SE',
  'SI',
  'SK',
];

export const SOUTH_AMERICA_CODES = [
  'AR',
  'BO',
  'BR',
  'CL',
  'CO',
  'EC',
  'FK',
  'GF',
  'GY',
  'PY',
  'PE',
  'SR',
  'UY',
  'VE',
];

export const productsRef = collection(
  db,
  'products'
) as CollectionReference<ProductDocument>;

export const usersRef = collection(
  db,
  'users'
) as CollectionReference<UserDocument>;

const fetchBundle = async () => {
  try {
    const url = process.env.NEXT_PUBLIC_IS_STAGING
      ? 'https://storage.googleapis.com/mx-locker-staging.appspot.com/common-files/firestore-bundle.txt'
      : 'https://bundle.mxlocker.com/firestore-bundle.txt';
    const res = await fetch(url, {
      // Add Next.js cache configuration to prevent prerendering errors
      next: { revalidate: 3600 }, // Revalidate every hour
    });
    const text = await res.text();
    await loadBundle(db, text);
  } catch (e) {
    logError('homeBundle: ' + (e as Error).message);
  }
};

export const getSalePageData = async (slug: string) => {
  const url = `https://us-central1-${process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID}.cloudfunctions.net/v2_getSalePage`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      data: {
        slug,
      },
    }),
  });
  const data = (await res.json()) as { result: SalePageResponse };
  return data.result;
};

export const getProductsByIdQuery = (ids: string[]) =>
  query(productsRef, where('id', 'in', [...ids]));

export const getAuctionsQuery = () =>
  query(
    productsRef,
    where('is_auction', '==', true),
    where('out_of_stock', '==', false),
    // order by ending soonest
    orderBy('end_time', 'asc'),
    limit(10)
  );

export async function getProductBySlug(
  args: GetProductArgs
): Promise<ApiResponse<ProductDocument | null>> {
  // Cache is handled by the calling function (fetchProduct in page.tsx) which uses 'use cache'
  if (args.slug && args.slug.length === 16 && !isNaN(args.slug as any)) {
    const product = await getProductByItemNumber(+args.slug);
    if (product) return { results: product };
  }
  const q = query(productsRef, where('slug', '==', args.slug), limit(1));
  const snapshot = await getDocs(q);
  const data = snapshot.docs?.[0]?.data() ?? null;
  if (!data) {
    const product = await getProductById(args.slug ?? '');
    if (product) return { results: product };
    return { results: null };
  }
  return { results: data };
}

export async function getProductByItemNumber(
  num: number
): Promise<ProductDocument | null> {
  const q = query(productsRef, where('item_number', '==', num), limit(1));
  const snapshot = await getDocs(q);
  return snapshot.docs?.[0]?.data() ?? null;
}

export async function getProductByIdNotClean(id: string) {
  const d = doc(productsRef, id);
  const snapshot = await getDoc(d);
  return snapshot.exists() ? snapshot.data() : null;
}

export async function getProductById(id: string) {
  const d = doc(productsRef, id);
  const snapshot = await getDoc(d);
  return snapshot.exists() ? cleanLocation(snapshot.data()) : null;
}

export async function getProductBySku(sku: string): Promise<DataDepot | null> {
  const normalizedSku = normalize(sku);
  if (!normalizedSku) return null;
  const dataDepotRef = collection(db, 'data_depot');
  const res = await getDocs(
    query(
      dataDepotRef,
      where('normalized_skus', 'array-contains', normalizedSku)
    )
  );
  const results = res.docs.map((doc) => doc.data());

  if (results.length) {
    return results[0] as DataDepot;
  }

  return null;
}

export async function getProductByMpn(mpn: string[]) {
  const dataDepotRef = collection(db, 'data_depot');
  const docs = [];
  const chunks = mpn.reduce((acc, curr, index) => {
    const chunkIndex = Math.floor(index / 30);
    if (!acc[chunkIndex]) {
      acc[chunkIndex] = [];
    }
    acc[chunkIndex].push(curr);
    return acc;
  }, [] as string[][]);
  for (const chunk of chunks) {
    const res = await getDocs(
      query(
        dataDepotRef,
        where('normalized_skus', 'array-contains-any', chunk.map(normalize))
      )
    );
    docs.push(...res.docs.map((doc) => doc.data()));
  }
  return (docs[0] ?? null) as DataDepotProduct | null;
}

export async function getProductFromDataDepotById(id: string) {
  const dataDepotRef = collection(db, 'data_depot');
  const d = doc(dataDepotRef, id);
  const snapshot = await getDoc(d);
  return snapshot.exists() ? (snapshot.data() as ProductDocument) : null;
}

export async function getMostRecentProductSlugs(): Promise<
  ApiResponse<string[]>
> {
  const q = query(productsRef, orderBy('date_added', 'desc'), limit(1000));
  const docsResponse = await getDocs(q);
  const slugs = docsResponse.docs.map((doc) => doc.get('slug')).filter(Boolean);
  return {
    results: slugs,
  };
}

export async function getProductsByCategory(categories: string[]) {
  const q1 = query(
    productsRef,
    where('categories', 'array-contains', categories[0]),
    where('out_of_stock', '==', false),
    orderBy('date_added', 'desc'),
    limit(categories.length === 1 ? 20 : 10)
  );

  if (categories.length === 1) {
    const snapshot1 = await getDocs(q1);
    return [[...snapshot1.docs.map((doc) => cleanLocation(doc.data()))]];
  }

  const q2 = query(
    productsRef,
    where('categories', 'array-contains', categories[1]),
    where('out_of_stock', '==', false),
    orderBy('date_added', 'desc'),
    limit(10)
  );
  const snapshot1 = await getDocs(q1);
  const snapshot2 = await getDocs(q2);

  const products = [
    [...snapshot1.docs.map((doc) => cleanLocation(doc.data()))],
    [...snapshot2.docs.map((doc) => cleanLocation(doc.data()))],
  ];
  return products;
}

export async function getProductsByIds(
  ids: string[],
  dontClean?: boolean
): Promise<ProductDocument[]> {
  if (ids.length === 0) return [];

  const chunkSize = 30;
  const chunks = [];
  for (let i = 0; i < ids.length; i += chunkSize) {
    chunks.push(ids.slice(i, i + chunkSize));
  }

  const products: ProductDocument[] = [];
  for (const chunk of chunks) {
    const q = query(productsRef, where('id', 'in', chunk));
    const snapshot = await getDocs(q);
    products.push(
      ...snapshot.docs.map((d) =>
        dontClean ? d.data() : cleanLocation(d.data())
      )
    );
  }

  return products;
}

export async function getProductsBySellerId(
  sellerId: string,
  l = DEFAULT_LIMIT,
  currProductId?: string
): Promise<ProductDocument[]> {
  const q = query(
    productsRef,
    where('seller_id', '==', sellerId),
    where('out_of_stock', '==', false),
    orderBy('date_added', 'desc'),
    limit(l),
    ...(currProductId ? [where('id', '!=', currProductId)] : [])
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => cleanLocation(doc.data())) ?? [];
}

export async function getLiveListingsCountFromTypesense(
  sellerId: string
): Promise<number> {
  if (typeof window !== 'undefined' && isBot(window.navigator.userAgent)) {
    return 0;
  }
  return getLiveListingsCountServer(sellerId);
}

export async function getDraftProductsBySellerId(seller_id: string) {
  const q = query(
    productsRef,
    where('seller_id', '==', seller_id),
    where('is_draft', '==', true)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => cleanLocation(doc.data())) ?? [];
}

export async function getProductsByPurchaseEntryID(
  purchaseEntryID: string
): Promise<ProductDocument[]> {
  const q = query(
    productsRef,
    where('purchase_entry_id', '==', purchaseEntryID),
    orderBy('date_added', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => cleanLocation(doc.data())) ?? [];
}

export interface GetDirtBikesArgs {
  page: number;
  onSale?: boolean;
  types: string[];
  bikeMakes: string[];
  bikeModels: string[];
  bikeYears: string[];
  engineSizes: string[];
  conditions: Condition[];
  minPrice: number | null;
  maxPrice: number | null;
  sort: ProductSortOption;
  location?: {
    lat: number;
    lng: number;
    address: string;
  } | null;
  radius?: number;
  keyword: string;
  brands: string[];
  is_featured?: boolean;
  letterSizes: string[];
  numberSizes: string[];
}

export const getStaticMapUrl = (lat: number, lng: number) => {
  // Don't want to give away exact location :)
  lat = +lat.toFixed(2);
  lng = +lng.toFixed(2);
  const size = isMobile() ? '300x300' : '500x500';
  var uri = 'https://maps.googleapis.com/maps/api/staticmap?';
  var staticMapSrc = 'center=' + lat + ',' + lng;
  staticMapSrc += '&zoom=6';
  staticMapSrc += `&key=${process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY}`;
  staticMapSrc += '&size=' + size;
  return uri + encodeURI(staticMapSrc);
};

type SearchRequestBody = {
  params: Pick<
    SearchParamsWithPreset<DocumentSchema, ''>,
    | 'q'
    | 'page'
    | 'per_page'
    | 'filter_by'
    | 'facet_by'
    | 'max_facet_values'
    | 'sort_by'
    | 'preset'
  >;
  user?: Pick<UserDocument, 'sports' | 'sizes' | 'gender'>;
  uid?: string;
};

export function getTypesenseSortBy(sort: ProductSortOption) {
  if (sort === 'priceAsc') return 'price:asc';
  if (sort === 'priceDesc') return 'price:desc';
  if (sort === 'newest') return `date_added:desc`;
  if (sort === 'bestMatch') return '';
  if (sort === 'soldDate') return 'sold_date:desc';
  return 'date_added:desc';
}

function sportToRideType(sport: keyof Sports): RideType {
  switch (sport) {
    case 'mx':
      return 'Dirt Bikes';
    case 'snow':
      return 'Snow';
    case 'atv':
      return 'ATV';
    case 'utv':
      return 'UTV';
    case 'street':
      return 'Street';
    case 'adv':
      return 'Adventure';
    case 'cycling':
      return 'Cycling';
    case 'mtb':
      return 'Mountain Bikes';
    case 'pwc':
      return 'PWC';
    case 'emoto':
      return 'E-Moto';
    default:
      return 'Dirt Bikes';
  }
}

function getRideTypesFromSports(sports: Sports) {
  const ride_types: RideType[] = ['Dirt Bikes'];
  if (sports)
    for (const sport in sports) {
      const key = sport as keyof Sports;
      if (sports[key]) {
        const rt = sportToRideType(key);
        if (rt !== 'Dirt Bikes') ride_types.push(rt);
      }
    }
  return ride_types;
}

// BEGIN: for-you product fetchers

/**
 * Replicates the functionality of getProducts very closely. Duplicated b/c getProducts is
 * used only in the context of getServerSideProps, where its args are provided from the same
 * context every time (server-side). Might be nice to generalize in the future.
 */
export async function getForYouProducts({
  brand,
  category,
  subCategory,
  userBike,
  userSizes,
  searchLimit = DEFAULT_LIMIT,
  sort = 'mostPopular',
  lastDocSnapshot,
}: GetForYouProductsOptions): ProductResultsPaginatedByRef {
  // Set defaults

  const resultLimit = searchLimit ?? DEFAULT_LIMIT;
  const brands = brand ? [brand] : [];

  // Build Firestore query constraints

  let baseConstraints = [
    where('out_of_stock', '==', false),
    getSortConstraint(sort),
    getBrandsConstraint(brands),
  ];

  if (!!category) {
    baseConstraints.push(getCategoryConstraint(category));
  }

  if (!!subCategory) {
    baseConstraints.push(getSubCategoryConstraint(subCategory, null, null));
  }

  if (!!userSizes && !!category && !!subCategory) {
    if (!!userSizes.boots && subCategory === 'Boots') {
      baseConstraints.push(getBootSizesConstraint([`${userSizes?.boots}`]));
    }
    if (!!userSizes.helmet && subCategory === 'Helmets') {
      baseConstraints.push(getHelmetSizesConstraint([userSizes?.helmet]));
    }
    if (!!userSizes.jersey && subCategory === 'Jerseys') {
      baseConstraints.push(getShirtSizesConstraint([userSizes?.jersey]));
    }
    if (!!userSizes.pants && subCategory === 'Pants') {
      baseConstraints.push(getPantSizesConstraint([`${userSizes?.pants}`]));
    }
    if (!!userSizes.gloves && subCategory === 'Gloves') {
      baseConstraints.push(getGloveSizesConstraint([userSizes?.gloves]));
    }
  }

  if (!!userBike) {
    let bikeConstraints = {
      makes: [] as string[],
      models: [] as string[],
      years: [] as string[],
    };
    if (!!userBike.make) {
      bikeConstraints.makes = [userBike.make];
    }
    if (!!userBike.model) {
      bikeConstraints.models = [userBike.model];
    }
    if (!!userBike.year) {
      bikeConstraints.years = [`${userBike.year}`];
    }
    baseConstraints.push(getBikeConstraints(bikeConstraints));
  }

  // Build Firestore query and fetch data

  const nonNullConstraints = baseConstraints.filter(nonNullable);

  const queryBase = query(
    productsRef,
    ...nonNullConstraints,
    limit(resultLimit)
  );

  const qFinal = lastDocSnapshot
    ? query(queryBase, startAfter(lastDocSnapshot))
    : queryBase;

  const docsSnapshot = await getDocs(qFinal);

  const docsResponse = docsSnapshot.docs.map((x) => cleanLocation(x.data()));

  // For pagination
  const lastVisible = docsSnapshot.docs[docsSnapshot.docs.length - 1];

  return {
    results: docsResponse,
    brands: getBrandsFromResult(docsResponse),
    lastDocSnapshot: lastVisible,
  };
}

/**
 * Replicated the functionality of getProducts very closely but excluding filters.
 * Should only be used to fetch personalized+unfiltered results from Typesense.
 * See getForYouProducts for information on why it was decided to replicate this.
 */
export async function getForYouProductsFromTypesense(
  searchLimit: number = DEFAULT_LIMIT,
  page: number = 1,
  category?: string
) {
  return getForYouProductsFromTypesenseServer(searchLimit, page, category);
}

// END: for-you product fetchers

const mainCats: string[] = ['Riding Gear', 'Bike Parts', 'Collectibles'];

export async function getNewArrivals(
  l = DEFAULT_LIMIT
): Promise<ApiResponse<ProductDocument[]>> {
  if (process.env.NODE_ENV === 'production') {
    const nQuery = await namedQuery(db, 'recent-products-query');
    if (nQuery) {
      const { docs } = await getDocsFromCache(nQuery);
      const results = docs.map((d) =>
        cleanLocation(d.data() as ProductDocument)
      );
      return { results };
    } else fetchBundle();
  }
  const q = query(
    productsRef,
    where('category', 'in', mainCats),
    where('out_of_stock', '==', false),
    orderBy('date_added', 'desc'),
    limit(l)
  );
  const { docs } = await getDocs(q);
  const results = docs.map((d) => cleanLocation(d.data()));
  return { results };
}

export async function getPageNewArrivals(
  l = DEFAULT_LIMIT,
  page: string,
  categoryFilter?: string
): Promise<{
  results: ProductDocument[];
}> {
  const categoryMap: { [key: string]: string } = {
    '/shop/riding-gear': 'Riding Gear',
    '/shop/parts': 'Bike Parts',
    '/shop/accessories': 'Accessories',
  };

  const category = categoryMap[page];
  const conditions = [
    where('out_of_stock', '==', false),
    orderBy('date_added', 'desc'),
    limit(l),
  ];

  if (categoryFilter) {
    conditions.push(where('categories', 'array-contains', categoryFilter));
  } else {
    conditions.push(where('category', 'in', mainCats));
  }

  if (category) {
    conditions.push(where('category', '==', category));
  }

  const q = query(productsRef, ...conditions);
  const { docs } = await getDocs(q);
  const results = docs.map((d) => cleanLocation(d.data() as ProductDocument));
  return { results };
}

export async function getFeaturedProducts(l = DEFAULT_LIMIT) {
  if (process.env.NODE_ENV === 'production') {
    const nQuery = await namedQuery(db, 'featured-products-query');
    if (nQuery) {
      const { docs } = await getDocsFromCache(nQuery);
      const results = docs.map((d) =>
        cleanLocation(d.data() as ProductDocument)
      );
      return { results };
    } else fetchBundle();
  }
  const q = query(
    productsRef,
    where('is_featured', '==', true),
    where('category', 'in', mainCats),
    where('out_of_stock', '==', false),
    orderBy('date_added', 'desc'),
    limit(l)
  );
  const { docs } = await getDocs(q);
  const results = docs.map((d) => d.data());
  return { results };
}

export async function getFeaturedBikes(l = DEFAULT_LIMIT) {
  if (process.env.NODE_ENV === 'production') {
    const nQuery = await namedQuery(db, 'featured-bikes-query');
    if (nQuery) {
      const { docs } = await getDocsFromCache(nQuery);
      const results = docs.map((d) =>
        cleanLocation(d.data() as ProductDocument)
      );
      return { results };
    } else fetchBundle();
  }
  const q = query(
    productsRef,
    where('is_featured', '==', true),
    where('category', 'in', VEHICLE_CATEGORIES),
    where('out_of_stock', '==', false),
    orderBy('date_added', 'desc'),
    limit(l)
  );
  const { docs } = await getDocs(q);
  const results = docs.map((d) => cleanLocation(d.data()));
  return { results };
}

export async function getRecentBikes(l = DEFAULT_LIMIT) {
  if (process.env.NODE_ENV === 'production') {
    const nQuery = await namedQuery(db, 'recent-bikes-query');
    if (nQuery) {
      const { docs } = await getDocsFromCache(nQuery);
      const results = docs.map((d) =>
        cleanLocation(d.data() as ProductDocument)
      );
      return { results };
    } else fetchBundle();
  }
  const q = query(
    productsRef,
    where('category', 'in', VEHICLE_CATEGORIES),
    where('out_of_stock', '==', false),
    orderBy('date_added', 'desc'),
    limit(l)
  );
  const { docs } = await getDocs(q);
  const results = docs.map((d) => cleanLocation(d.data()));
  return { results };
}

export async function getAllAuctionsBySellerId(seller_id: string) {
  const q = query(
    productsRef,
    where('seller_id', '==', seller_id),
    where('is_auction', '==', true),
    // where('auction_name', '==', 'Brian Barnes Ironman'),
    where('is_draft', '==', false),
    where('end_time', '>=', Date.now())
  );
  const { docs } = await getDocs(q);
  const results = docs.map((d) => d.data());
  return { results };
}

export async function getLiveAuctions() {
  const q = query(
    productsRef,
    where('is_auction', '==', true),
    where('out_of_stock', '==', false),
    where('is_auction_live', '==', true),
    where('end_time', '>=', Date.now()),
    orderBy('end_time', 'asc'),
    limit(30)
  );

  return await getDocs(q).then((res) =>
    res.docs.map((d) => getProductLight(d.data()))
  );
}

export async function getOnSale(l = DEFAULT_LIMIT) {
  if (process.env.NODE_ENV === 'production') {
    const nQuery = await namedQuery(db, 'onsale-products-query');
    if (nQuery) {
      const { docs } = await getDocsFromCache(nQuery);
      const results = docs.map((d) =>
        cleanLocation(d.data() as ProductDocument)
      );
      return { results };
    } else fetchBundle();
  }
  const q = query(
    productsRef,
    where('on_sale', '==', true),
    where('category', 'in', mainCats),
    where('out_of_stock', '==', false),
    orderBy('date_added', 'desc'),
    limit(l)
  );
  const { docs } = await getDocs(q);
  const results = docs.map((d) => d.data());
  return { results };
}

export async function getRecentlySold(l = DEFAULT_LIMIT) {
  if (process.env.NODE_ENV === 'production') {
    const nQuery = await namedQuery(db, 'sold-products-query');
    if (nQuery) {
      const { docs } = await getDocsFromCache(nQuery);
      const results = docs.map((d) =>
        cleanLocation(d.data() as ProductDocument)
      );
      return { results };
    } else fetchBundle();
  }
  const q = query(productsRef, orderBy('sold_date', 'desc'), limit(l));
  const { docs } = await getDocs(q);
  const results = docs.map((d) => cleanLocation(d.data()));
  return { results };
}

export async function getRecentMTB() {
  if (process.env.NODE_ENV === 'production') {
    const nQuery = await namedQuery(db, 'recent-mountain-bikes-query');
    if (nQuery) {
      const { docs } = await getDocsFromCache(nQuery);
      const results = docs.map((d) =>
        cleanLocation(d.data() as ProductDocument)
      );
      return { results };
    }
  }
  const q = query(
    productsRef,
    where('category', '==', 'Mountain Bikes'),
    where('out_of_stock', '==', false),
    orderBy('date_added', 'desc'),
    limit(12)
  );
  const { docs } = await getDocs(q);
  const results = docs.map((d) => cleanLocation(d.data()));
  return { results };
}

export async function getRelatedProducts(
  product: ProductDocument,
  l = DEFAULT_LIMIT
) {
  if (typeof window !== 'undefined' && isBot(window.navigator.userAgent)) {
    return {
      results: [],
    };
  }
  return getRelatedProductsServer(product, l);
}

export async function getMoreFromThisSeller(
  product: ProductDocument,
  l = DEFAULT_LIMIT
): Promise<ProductLight[]> {
  const q = query(
    productsRef,
    where('seller_id', '==', product.seller_id),
    where('out_of_stock', '==', false),
    orderBy('date_added', 'desc'),
    limit(l)
  );
  const snapshot = await getDocs(q);
  const products =
    snapshot.docs.map((doc) => getProductLight(doc.data())) ?? [];
  return products.filter((p) => p.id !== product.id) ?? [];
}

export async function getSellerListingsPaginated(
  sellerId: string,
  isAdmin: boolean,
  page: number,
  category: string | null = null,
  category1: string | null = null,
  brand: string | null = null,
  condition: Condition | 'all' = 'all',
  availability: Availability | 'all' = 'all',
  auctionName: string | null = 'all',
  l: number = 12
) {
  if (!sellerId && !isAdmin)
    return Promise.resolve({
      results: [],
      pageInfo: {
        page: 1,
        totalPages: 1,
      },
    });

  const limitValue = availability === 'paymentFailed' ? null : l;

  const filterConstraints = [
    ...(category !== 'all' ? [where('category', '==', category)] : []),
    ...(category1 !== 'all' ? [where('category1', '==', category1)] : []),
    ...(condition !== 'all' ? [where('condition', '==', condition)] : []),
    ...(brand !== 'all' ? [where('brand', '==', brand)] : []),
    ...(availability !== 'all'
      ? [...getAvailabilityConstraint(availability)]
      : []),
    ...(auctionName !== 'all'
      ? [where('auction_name', '==', auctionName)]
      : []),
  ];

  const orderByField =
    availability === 'notSold' || availability === 'paymentFailed'
      ? null
      : 'date_added';

  const noLimitQ = query(
    productsRef,
    where('seller_id', '==', sellerId),
    ...filterConstraints,
    ...(orderByField ? [orderBy(orderByField, 'desc')] : [])
  );
  const noLimitAdminQ = query(
    productsRef,
    ...filterConstraints,
    ...(orderByField ? [orderBy(orderByField, 'desc')] : [])
  );
  const count = (
    await getCountFromServer(isAdmin ? noLimitAdminQ : noLimitQ)
  ).data().count;
  const totalPages = limitValue ? Math.ceil(count / limitValue) : 1;

  // handle pagination
  let lastVisible = null;
  if (page > 1 && page <= totalPages) {
    const q = query(
      productsRef,
      where('seller_id', '==', sellerId),
      ...filterConstraints,
      ...(orderByField ? [orderBy(orderByField, 'desc')] : []),
      ...(limitValue ? [limit((page - 1) * limitValue)] : [])
    );

    const adminQ = query(
      productsRef,
      ...filterConstraints,
      ...(orderByField ? [orderBy(orderByField, 'desc')] : []),
      ...(limitValue ? [limit((page - 1) * limitValue)] : [])
    );

    const snap = await getDocs(isAdmin ? adminQ : q);
    lastVisible = snap.docs[snap.docs.length - 1];
  }

  const q = query(
    productsRef,
    where('seller_id', '==', sellerId),
    ...filterConstraints,
    ...(orderByField ? [orderBy(orderByField, 'desc')] : []),
    ...(lastVisible ? [startAfter(lastVisible)] : []),
    ...(limitValue ? [limit(limitValue)] : [])
  );
  const adminQ = query(
    productsRef,
    ...filterConstraints,
    ...(orderByField ? [orderBy(orderByField, 'desc')] : []),
    ...(lastVisible ? [startAfter(lastVisible)] : []),
    ...(limitValue ? [limit(limitValue)] : [])
  );

  const snap = await getDocs(isAdmin ? adminQ : q);

  let results = snap.docs.map((d) => d.data() as ProductDocument);
  if (availability === 'paymentFailed') {
    results = results.filter((r) => r.og_price !== r.price);
  }
  return {
    results,
    pageInfo: {
      page,
      totalPages,
    },
  };
}

export async function deleteListing(productId: string) {
  const docRef = doc(productsRef, productId);
  await updateDoc(docRef, {
    is_draft: false,
    date_updated: Date.now(),
    date_added: deleteField(),
    out_of_stock: true,
  });
}

export async function markAsSold(product: ProductDocument): Promise<void> {
  const bikeInfo: {
    item_id: string;
    value: number;
    year?: number;
    make?: string;
    model?: string;
  } = { item_id: product.id, value: product.price };
  if (product.year) bikeInfo.year = product.year;
  if (product.make) bikeInfo.make = product.make;
  if (product.model) bikeInfo.model = product.model;
  logEvent('bike_sold', bikeInfo);
  const ref = doc(productsRef, product.id);
  return updateDoc(ref, {
    stock: 0,
    out_of_stock: true,
    sold_date: Date.now(),
    date_added: deleteField(),
  });
}

// we'll discuss this one
// prob use our ai guy
export async function getFreqPurchasedWith(
  categories: string[],
  l = DEFAULT_LIMIT
) {
  const q = query(
    productsRef,
    where(`category${categories.length - 1}`, '==', categories.pop()),
    where('out_of_stock', '==', false),
    orderBy('date_added', 'desc'),
    limit(l)
  );
  const { docs } = await getDocs(q);
  const results = docs.map((d) => cleanLocation(d.data()));
  return { results };
}

export function getProductId(): string {
  const newDocRef = doc(productsRef);
  const id = newDocRef.id;
  return id;
}

export async function addProductDoc(data: ProductDocument) {
  const parsedData = productSchema.parse(data);
  const docRef = doc(productsRef, parsedData.id);

  Object.keys(parsedData).forEach(
    (key) =>
      // @ts-ignore typescript doesnt' like this
      parsedData[key] === undefined && delete parsedData[key]
  );
  return setDoc(docRef, parsedData);
}

export async function updateProductListingDoc(data: ProductDocument) {
  const parsedData = productSchema.parse(data);
  const docRef = doc(productsRef, parsedData.id);
  // Not using { merge: true} because the edit listing page makes sure the product passed is fully correct
  return setDoc(docRef, parsedData);
}

export async function addProductDraftDoc(data: Partial<ProductDocument>) {
  // filter out any undefined values, todo do this better
  removeUndefinedRecursive(data);
  const docRef = doc(productsRef, data.id);
  return setDoc(docRef, data);
}

export async function addProductToDataDepot(data: ProductDocument) {
  return await httpsCallable<{ product: ProductDocument }>(
    functions,
    'dataDepot-v2_addProductToDataDepot'
  )({ product: data });
}

export function removeUndefinedRecursive(obj: any) {
  Object.keys(obj).forEach((key) => {
    if (obj[key] && typeof obj[key] === 'object') {
      removeUndefinedRecursive(obj[key]);
    } else if (obj[key] === undefined) {
      delete obj[key];
    }
  });
  return obj;
}

export async function updateProductDraftDoc(data: Partial<ProductDocument>) {
  const docRef = doc(productsRef, data.id);
  removeUndefinedRecursive(data);
  return updateDoc(docRef, data);
}

export async function reactivateProductDoc(product_id: string) {
  const docRef = doc(productsRef, product_id);
  return updateDoc(docRef, {
    date_updated: Date.now(),
    is_draft: false,
    out_of_stock: false,
  });
}

// Constraints
function getBikeConstraints(bikes: {
  years: string[];
  makes: string[];
  models: string[];
}) {
  const allBikeCombos = [];
  const allBikeMakes = [];
  const allBikeMakesModels = [];
  if (!bikes.makes?.length) return null;

  for (const make of bikes.makes) {
    allBikeMakes.push({ make });
    for (const model of bikes.models) {
      allBikeMakesModels.push({ make, model });
      for (const year of bikes.years) {
        allBikeCombos.push({ year, make, model });
      }
    }
  }
  if (
    !allBikeCombos.length &&
    !allBikeMakes.length &&
    !allBikeMakesModels.length
  )
    return null;
  return where('fitting_bikes.bikes', 'array-contains-any', [
    ...(allBikeMakes.length ? allBikeMakes.map((b) => ({ make: b.make })) : []),
    ...(allBikeMakesModels.length
      ? allBikeMakesModels.map((b) => ({ make: b.make, model: b.model }))
      : []),
    ...(allBikeCombos.length
      ? allBikeCombos.map((b) => ({
          make: b.make,
          model: b.model,
          year: +b.year,
        }))
      : []),
  ]);
}
function getCategoryConstraint(
  categoryQueryValue: string | null,
  showDirtBikes?: boolean
) {
  return !!categoryQueryValue
    ? where('category', '==', categoryQueryValue)
    : where('category', 'in', [
        'Riding Gear',
        'Mountain Bikes',
        'Bike Parts',
        'Collectibles',
        'Accessories',
        ...(showDirtBikes ? ['Dirt Bikes'] : []),
      ]);
}

function getSubCategoryConstraint(
  cat1: string | null,
  cat2: string | null,
  cat3: string | null
) {
  return !!cat3
    ? where('category3', '==', cat3)
    : !!cat2
    ? where('category2', '==', cat2)
    : !!cat1
    ? where('category1', '==', cat1)
    : null;
}

function getSortConstraint(sortQueryValue?: string | null) {
  return sortQueryValue === 'priceAsc'
    ? orderBy('price', 'asc')
    : sortQueryValue === 'priceDesc'
    ? orderBy('price', 'desc')
    : orderBy('date_added', 'desc');
}

function getBrandsConstraint(brandsQueryValue?: string[] | null) {
  if (brandsQueryValue?.length) {
    if (brandsQueryValue.length === 1) {
      return where('brand', '==', brandsQueryValue[0]);
    } else if (brandsQueryValue.length > 1) {
      return where('brand', 'in', brandsQueryValue);
    }
  }
  return null;
}

function getBootSizesConstraint(bootSizesQueryValue?: string[] | null) {
  let numArray: number[] = [];
  if (bootSizesQueryValue?.length)
    numArray = bootSizesQueryValue.map((c) => +c);
  return !bootSizesQueryValue?.length
    ? null
    : bootSizesQueryValue.length === 1
    ? where('size.number', '==', +numArray[0])
    : where('size.number', 'in', numArray);
}

function getShirtSizesConstraint(shirtSizeConstraint?: string[] | null) {
  if (shirtSizeConstraint?.length)
    shirtSizeConstraint = shirtSizeConstraint.map((c) => c);
  return !shirtSizeConstraint?.length
    ? null
    : shirtSizeConstraint.length === 1
    ? where('size.letter', '==', shirtSizeConstraint[0])
    : where('size.letter', 'in', shirtSizeConstraint);
}

function getPantSizesConstraint(pantSizeConstraint?: string[] | null) {
  if (pantSizeConstraint?.length)
    pantSizeConstraint = pantSizeConstraint.map((c) => c);
  return !pantSizeConstraint?.length
    ? null
    : pantSizeConstraint.length === 1
    ? where('size.number', '==', +pantSizeConstraint[0])
    : where(
        'size.number',
        'in',
        pantSizeConstraint.map((c) => +c)
      );
}

/**
 * Return the size constraint filter for Firestore objects with the size.letter
 * attribute (e.g. gloves and helmets).
 */
function getLetterSizeConstraint(letterSizeConstraint?: string[] | null) {
  if (letterSizeConstraint?.length)
    letterSizeConstraint = letterSizeConstraint.map((c) => c);
  return !letterSizeConstraint?.length
    ? null
    : letterSizeConstraint.length === 1
    ? where('size.letter', '==', letterSizeConstraint[0])
    : where('size.letter', 'in', letterSizeConstraint);
}

function getGloveSizesConstraint(gloveSizeConstraint?: string[] | null) {
  return getLetterSizeConstraint(gloveSizeConstraint);
}

function getHelmetSizesConstraint(helmetSizeConstraint?: string[] | null) {
  return getLetterSizeConstraint(helmetSizeConstraint);
}

// Helpers
function argsRequireTypesense(
  args: Pick<
    BaseGetProductsArgs,
    | 'keyword'
    | 'page'
    | 'minPrice'
    | 'maxPrice'
    | 'brands'
    | 'conditions'
    | 'sort'
    | 'shipTo'
    | 'sellerType'
    | 'bikeMakes'
    | 'bikeModels'
    | 'bikeYears'
    | 'letterSizes'
    | 'numberSizes'
  >
) {
  return (
    args.sellerType ||
    args.keyword ||
    args.page > 1 ||
    ((args.minPrice || args.maxPrice) && args.sort === 'newest') ||
    (args.brands &&
      args.brands.length > 1 &&
      args.conditions &&
      args.conditions.length > 1) ||
    args.brands?.length > 30 ||
    (args.shipTo && args.shipTo.length >= 1) ||
    (args.bikeMakes && args.bikeMakes.length >= 1) ||
    (args.bikeModels && args.bikeModels.length >= 1) ||
    (args.bikeYears && args.bikeYears.length >= 1) ||
    (args.letterSizes && args.letterSizes.length >= 1) ||
    (args.numberSizes && args.numberSizes.length >= 1)
  );
}

export async function reportItem(
  id: string,
  reason: ReasonTypes,
  isUser: boolean
): Promise<void> {
  const col = isUser ? 'reports_users' : 'reports_products';
  const colRef = collection(db, col);
  const ref = doc(colRef, id);
  return setDoc(ref, { [reason]: increment(1) }, { merge: true });
}

export async function getOnSaleByCategory(category: string, l = DEFAULT_LIMIT) {
  const q = query(
    productsRef,
    where('category', '==', category),
    where('on_sale', '==', true),
    where('out_of_stock', '==', false),
    orderBy('date_added', 'desc'),
    limit(l)
  );
  const { docs } = await getDocs(q);
  return docs.map((d) => cleanLocation(d.data()));
}

export async function getCuratedListByCategory(
  list: string,
  category: string,
  l = DEFAULT_LIMIT
) {
  const q = query(
    productsRef,
    where('category', '==', category),
    where('curated_lists', 'array-contains', list),
    where('out_of_stock', '==', false),
    orderBy('date_added', 'desc'),
    limit(l)
  );
  const { docs } = await getDocs(q);
  return docs.map((d) => cleanLocation(d.data()));
}

export async function updateProductByKey(
  id: string,
  key: keyof ProductDocument,
  value: any
): Promise<void> {
  const ref = doc(productsRef, id);
  return updateDoc(ref, { [key]: value });
}

export async function updateProductByKeys(
  id: string,
  keyValues: Record<string, any>
): Promise<void> {
  const ref = doc(productsRef, id);
  return updateDoc(ref, keyValues);
}

export async function bumpProduct(id: string): Promise<void> {
  const ref = doc(productsRef, id);
  return updateDoc(ref, { date_added: Date.now(), bumps: increment(-1) });
}

export async function removeProductFromFavorites(
  uid: string,
  productId: string
): Promise<void> {
  const ref = doc(usersRef, uid);
  return updateDoc(ref, { favorites: arrayRemove(productId) });
}

export async function addProductToFavorites(
  uid: string,
  productId: string
): Promise<void> {
  const ref = doc(usersRef, uid);
  return updateDoc(ref, { favorites: arrayUnion(productId) });
}

export async function addProductToCuratedList(
  productId: string,
  curated_list: string
): Promise<void> {
  const ref = doc(productsRef, productId);
  return updateDoc(ref, { curated_lists: arrayUnion(curated_list) });
}

export async function removeProductFromCuratedList(
  productId: string,
  curated_list: string
): Promise<void> {
  const ref = doc(productsRef, productId);
  return updateDoc(ref, { curated_lists: arrayRemove(curated_list) });
}

export async function getMasterProductInfo(cat2?: CategoryDocument): Promise<{
  min: number;
  max: number;
  cat1_name: string;
  brand_name: string;
  brand_slug: string;
}> {
  if (!cat2)
    return {
      min: 0,
      max: 0,
      cat1_name: '',
      brand_name: '',
      brand_slug: '',
    };
  const minPriceQ = query(
    productsRef,
    where('category2', '==', cat2.name),
    orderBy('price', 'asc'),
    limit(1)
  );

  const maxPriceQ = query(
    productsRef,
    where('category2', '==', cat2.name),
    orderBy('price', 'desc'),
    limit(1)
  );

  const [minPrice, maxPrice, cat1Snap, brand] = await Promise.all([
    getDocs(minPriceQ),
    getDocs(maxPriceQ),
    getCategoryByID(cat2.id),
    getBrandByName(cat2.name.split(' ')[0]),
  ]);
  return {
    min: minPrice.docs[0]?.get('price') ?? 0,
    max: maxPrice.docs[0]?.get('price') ?? 0,
    cat1_name: cat1Snap?.name ?? '',
    brand_name: brand?.name ?? '',
    brand_slug: brand?.url ?? '',
  };
}

export function setVacationMode(uid: string, on = false) {
  return httpsCallable<{ uid: string; on: boolean }, void>(
    functions,
    FirebaseCallable.vacationMode
  )({ uid, on });
}

const getBrandsFromResult = (products: ProductDocument[]) =>
  [...new Set(products.map((p) => p.brand))].sort().filter(Boolean);

const getAvailabilityConstraint = (availabilityQueryValue?: Availability) => {
  if (availabilityQueryValue === 'inStock') {
    return [where('out_of_stock', '==', false)];
  }
  if (availabilityQueryValue === 'sold') {
    return [where('out_of_stock', '==', true), where('is_draft', '==', false)];
  }
  if (availabilityQueryValue === 'draft') {
    return [where('is_draft', '==', true)];
  }
  if (availabilityQueryValue === 'onSale') {
    return [where('on_sale', '==', true), where('out_of_stock', '==', false)];
  }
  if (availabilityQueryValue === 'auction') {
    return [where('is_auction', '==', true)];
  }
  if (
    availabilityQueryValue === 'notSold' ||
    availabilityQueryValue === 'paymentFailed'
  ) {
    // p.is_auction && p.out_of_stock && p.stock
    return [
      where('is_auction', '==', true),
      where('out_of_stock', '==', true),
      where('stock', '>', 0),
    ];
  }
  return [];
};

export const getProductDescription = (product?: ProductDocument | null) => {
  if (!product) return '';
  if (!product.description && product.description_html) {
    return stripHtml(product.description_html).result;
  }
  return product.description;
};

export function getVariationPrice(
  product: ProductDocument,
  selectedVariations: Variation[] | null
) {
  if (!selectedVariations) return product.price;
  const letterSize = selectedVariations?.find((v) => isNaN(+v.size));
  const numberSize = selectedVariations?.find(
    (v) => !isNaN(+v.size) && v.is_number
  );
  const isColor = selectedVariations?.find((v) => !!v.color);
  if (letterSize && numberSize && !isColor) {
    return letterSize.price + numberSize.price;
  }
  if (letterSize) {
    if (isColor) {
      return Math.max(letterSize.price, isColor.price);
    }
    return letterSize.price;
  }
  if (numberSize) {
    if (isColor) {
      return Math.max(numberSize.price, isColor.price);
    }
    return numberSize.price;
  }
  return product.price;
}

export function getSizeFromVariation(selectedVariations: Variation[] | null) {
  const letterSize = selectedVariations?.find(
    (v) => v.size && !v.is_number && v.size !== 'N/A'
  );
  const numberSize = selectedVariations?.find(
    (v) => v.is_number && v.size && v.size !== 'N/A'
  );
  return {
    ...(letterSize && {
      letter: letterSize.size as string,
    }),
    ...(numberSize && {
      number: +numberSize.size,
    }),
  };
}

export type BikeGraphPoint = {
  price: number;
  sold_date: number;
};

export type BikeGraphData = {
  points: BikeGraphPoint[];
  min: number;
  max: number;
  count: number;
  avg: number;
};

export async function getBikeGraph(year: number, make: string, model: string) {
  const { data } = await httpsCallable<
    { year: number; make: string; model: string },
    BikeGraphData | null
  >(
    functions,
    FirebaseCallable.getBikeGraph
  )({ year, make, model });
  return data;
}

export async function getProductViews(id: string) {
  const productViewsRef = collection(db, 'product_views');
  const data = doc(productViewsRef, id);
  const docSnap = await getDoc(data);
  return (docSnap.data() as { views: number }) ?? { views: 0 };
}

export async function getCauses() {
  const causesRef = collection(db, 'causes');
  const q = query(causesRef);
  const { docs } = await getDocs(q);
  return docs.map((d) => d.data() as CauseDocument);
}

export async function getCauseById(cause: string) {
  const causesRef = collection(db, 'causes');
  const ref = doc(causesRef, cause);
  const docSnap = await getDoc(ref);
  if (!docSnap.exists()) return null;
  return docSnap.data() as CauseDocument;
}

export async function getReportedProducts() {
  const reportsRef = collection(db, 'reports_products');
  const q = query(reportsRef, limit(10));
  const { docs } = await getDocs(q);
  const res: Map<string, any> = new Map();
  docs.forEach((d) => {
    res.set(d.id, d.data());
  });
  const products = await getProductsByIds([...res.keys()]);
  const productsWithReports = products.map((p) => ({
    ...p,
    reports: res.get(p.id),
  }));
  return productsWithReports;
}

export async function getPartsForYourBikeFromRecommend(bike: Bike) {
  try {
    const res = await httpsCallable<{ bike: Bike }, ProductDocument[]>(
      functions,
      'v2_getShapedPartsForBike'
    )({ bike });
    return { results: res.data };
  } catch (e) {
    logError(e);
    return { results: [] };
  }
}

export async function getDirtBikeShipping({
  productId,
  zipCode,
  pickupZip,
  bikePrice,
}: {
  productId?: string;
  zipCode: string;
  pickupZip?: string;
  bikePrice?: number;
}) {
  const res = await httpsCallable<
    {
      productId?: string;
      zipCode: string;
      pickupZip?: string;
      bikePrice?: number;
    },
    {
      total: number; // amount to charge
      quote: number; // shipping before markup
      insurance_cost: number; // w markup
      shipping_cost: number; // w markup
    }
  >(
    functions,
    FirebaseCallable.calculateBikeShipping
  )({ productId, zipCode, pickupZip, bikePrice });
  return res.data;
}

export async function getInStockCount() {
  // const snap = await db
  //       .collection("products")
  //       .where("out_of_stock", "==", false)
  //       .orderBy("date_added", "desc")
  //       .get();

  const q = query(
    productsRef,
    where('out_of_stock', '==', false),
    orderBy('date_added', 'desc')
  );
  const count = await getCountFromServer(q);
  return count.data().count;
}

export async function setStoreOnSale({
  uid,
  salePercent,
}: {
  uid: string;
  salePercent: number;
}) {
  await httpsCallable<{ uid: string; salePercent: number }, void>(
    functions,
    FirebaseCallable.setStoreOnSale
  )({ uid, salePercent });
}

export async function sendOffersToLikers(product_id: string, price: number) {
  try {
    const sendOffer = httpsCallable<
      { product_id: string; price: number },
      { errorCount: number; successCount: number }
    >(functions, 'v2_sendOffersToLikers');

    const res = await sendOffer({ product_id, price });
    return { results: res.data };
  } catch (e) {
    logError(e);
    throw e;
  }
}

export async function getCategoryByProductCats(
  product: Omit<
    Pick<
      ProductDocument,
      'category' | 'category1' | 'category2' | 'category3' | 'ride_types'
    >,
    'category'
  > & { category: string | null },
  omitSEO = false
): Promise<CategoryDocument | null> {
  const colRef = collection(db, 'categories_new');
  const col1Ref = collectionGroup(db, 'category1_new');
  const col2Ref = collectionGroup(db, 'category2_new');
  const col3Ref = collectionGroup(db, 'category3_new');
  const col4Ref = collectionGroup(db, 'category4_new');
  const wheres = [
    product.ride_types?.length && product.category !== 'Collectibles'
      ? where('product_cats.ride_type', 'in', product.ride_types)
      : where('product_cats.ride_type', '==', null),
    where('product_cats.category', '==', product.category || null),
    where('product_cats.category1', '==', product.category1 || null),
    where('product_cats.category2', '==', product.category2 || null),
    where('product_cats.category3', '==', product.category3 || null),
    // where('product_cats.category4', '==', product.category4 || null), // one day
  ];
  const catDocQ = query(colRef, ...wheres, limit(1));
  const cat1DocQ = query(col1Ref, ...wheres, limit(1));
  const cat2DocQ = query(col2Ref, ...wheres, limit(1));
  const cat3DocQ = query(col3Ref, ...wheres, limit(1));
  const cat4DocQ = query(col4Ref, ...wheres, limit(1));
  const res = await Promise.all([
    getDocs(catDocQ),
    getDocs(cat1DocQ),
    getDocs(cat2DocQ),
    getDocs(cat3DocQ),
    getDocs(cat4DocQ),
  ]);
  const doc = res
    .flat()
    .find((doc) => !doc.empty && (!omitSEO || !doc.docs[0].data().is_seo_only));
  if (!doc) {
    if (product.category3) {
      return getCategoryByProductCats({ ...product, category3: null });
    } else if (product.category2) {
      return getCategoryByProductCats({ ...product, category2: null });
    } else if (product.category1) {
      return getCategoryByProductCats({ ...product, category1: null });
    } else if (product.category) {
      return getCategoryByProductCats({ ...product, category: null });
    } else if (product.ride_types?.length) {
      return getCategoryByProductCats({ ...product, ride_types: [] }, omitSEO);
    }
    return null;
  }
  return doc.docs[0].data() as CategoryDocument;
}

// use fetchBreadcrumbsServer instead if calling from a server component
export async function getBreadcrumbsClient(product: ProductDocument) {
  try {
    const hit = await getCategoryByProductCats(product);
    if (!hit?.breadcrumbs?.length) return DEFAULT_BREADCRUMBS;
    return formatBreadcrumbs(hit?.breadcrumbs, product);
  } catch (e) {
    logError(e);
    return DEFAULT_BREADCRUMBS;
  }
}

export async function deleteAllListings(uid: string) {
  // products
  const col = collection(db, 'products');
  const q = query(
    col,
    where('seller_id', '==', uid),
    where('out_of_stock', '==', false),
    orderBy('date_added', 'desc')
  );
  let count = (await getCountFromServer(q)).data().count;
  while (count) {
    const qWithLimit = query(q, limit(10));
    const { docs } = await getDocs(qWithLimit);
    const batch = writeBatch(db);
    docs.forEach((doc) => batch.delete(doc.ref));
    await batch.commit();
    count -= 10;
  }
}

export async function exportListings(uid: string) {
  const productsRef = collection(db, 'products');
  const q = query(
    productsRef,
    where('seller_id', '==', uid),
    where('out_of_stock', '==', false),
    orderBy('date_added', 'desc')
  );
  const { docs } = await getDocs(q);
  return docs.map((doc) => doc.data());
}

export async function importListings(file: File) {
  const products = JSON.parse(await file.text());
  const batch = writeBatch(db);
  products.forEach((product: ProductDocument) => {
    const ref = doc(productsRef, product.id);
    batch.set(ref, product);
  });
  await batch.commit();
}

export async function transferProductToSeller(
  pid: string,
  new_seller_id: string
) {
  const productsRef = collection(db, 'products');
  const ref = doc(productsRef, pid);
  await updateDoc(ref, {
    seller_id: new_seller_id,
    date_updated: Date.now(),
  });
}

export async function getProductsByWarehouseId(warehouseId: string) {
  const q = query(
    productsRef,
    where('integration_info.remote_id', '==', warehouseId),
    orderBy('date_added', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => doc.data() as ProductDocument);
}

export async function getProductsByWarehouseIDs(warehouseIds: string[]) {
  if (!warehouseIds.length) return [];
  const q = query(
    productsRef,
    where('integration_info.remote_id', 'in', warehouseIds),
    orderBy('date_added', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => doc.data() as ProductDocument);
}

export function hasValidVariations(
  product: ProductDocument,
  selectedVariations: Variation[] | null
) {
  if (!product.has_variations) return true;
  // Gear combos must have 2 variations selected
  if (product.category2 === 'Gear Combos') {
    return selectedVariations?.length === 2;
  }
  // if a product has color and sizes, must select both
  if (
    product.variations?.some((v) => v.color && v.color !== 'N/A') &&
    product.variations?.some((v) => v.size !== 'N/A')
  ) {
    const hasSelectedVariations = !!selectedVariations && selectedVariations[0];
    const hasValidColor =
      hasSelectedVariations &&
      selectedVariations[0].color &&
      selectedVariations[0].color !== 'N/A';
    const hasValidSize =
      hasSelectedVariations &&
      selectedVariations[0].size &&
      selectedVariations[0].size !== 'N/A';

    const selectedVariationHasSizeAndColor = hasValidColor && hasValidSize;

    if (selectedVariationHasSizeAndColor) return true;
    return selectedVariations?.length === 2;
  }
  // otherwise just one variation is needed
  return selectedVariations?.length === 1;
}

export function getInvalidVariationsText(
  product: ProductDocument,
  selectedVariations: Variation[] | null
) {
  if (!product.has_variations || !product.variations?.length) return;

  if (product.category2 === 'Gear Combos') {
    return 'Please select both a jersey and a pant';
  }

  if (selectedVariations?.length !== 1) {
    if (product.variations?.some((v) => v.color && v.color !== 'N/A')) {
      return 'Please select a color';
    } else {
      return 'Please select a size';
    }
  }
}

export type ProductCouponBundle = {
  account_id: string;
  active: boolean;
  amount_off: number | null;
  buyer_id: string | null;
  code: string;
  created: number;
  expires_at: number | null;
  first_purchase_only: boolean;
  id: string;
  is_private: boolean;
  max_per_customer: number | null;
  max_redemptions: number | null;
  max_amount: number | null;
  min_amount: number | null;
  percent_off: number | null;
  product_id: string | null;
  seller_id: string;
  redemptions: number;
};

export async function resyncTypesense(seller_id: string) {
  const res = await httpsCallable<
    { seller_id: string; delete_only?: boolean; cursor?: string },
    {
      successCount: number;
      errorCount?: number;
      nextCursor?: string | null;
    }
  >(
    functions,
    FirebaseCallable.resyncTypesenseListings
  )({ seller_id });
  return res.data;
}
